import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faNewspaper,
  faBullhorn,
  faLock,
  faEye,
  faEyeSlash,
  faCamera,
  faMicrophone,
  faUsers,
  faShieldAlt
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { saveUserData, redirectToDashboard } from "../../../utils/auth";

const RaiseLogin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("login"); // 'login' or 'otp'
  const [formData, setFormData] = useState({ email: "" });
  const [otp, setOtp] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value.toLowerCase().trim() });
    setError("");
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
    setError("");
  };

  // Step 1: Send OTP (adapted to use axios and your API style)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      // Send OTP request
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/raise/send/otp/login`, {
        email: formData.email
      },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("OTP send Response:", response);

      if (response.status === 200) {
        toast.success("OTP sent successfully");
        setStep("otp");
      } else {
        setError("Failed to send OTP");
      }
    } catch (err) {
      if (err.response.status === 500) {
        setError("Internal server error. Please try again later.");
      } else {
        if (err.response.status === 404) {
          toast.error("User not found with provided email address");
        } else {
          setError(err.response?.data?.message || "Failed to send OTP");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/raise/verify/otp/login`, {
        email: formData.email,
        loginOtp: otp
      });
      const data = response.data;
      if (data?.token) {
        // Save user data
        saveUserData({
          token: data.token,
          role: 'ryvuser',
          user: data.user || { role: 'ryvuser' }
        });

        // Get redirect path if it exists
        const redirectPath = localStorage.getItem('redirectPath');
        localStorage.removeItem('redirectPath'); // Clear it after use

        // Redirect to dashboard
        redirectToDashboard('ryvuser', navigate, redirectPath || '/raise/dashboard');
      } else {
        setError(data?.message || "OTP verification failed");
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError("Invalid OTP. Please try again.");
      } else {
        setError(err.response?.data?.message || "OTP verification failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 pt-20">
      <div className="w-full max-w-5xl mx-auto bg-white rounded-[2rem] shadow-2xl shadow-purple-200/50 overflow-hidden border border-slate-100">
        <div className="flex flex-col lg:flex-row min-h-[600px]">
          {/* Left side - Company Branding */}
          <div className="hidden lg:block lg:w-5/12 bg-gradient-to-br from-purple-700 via-fuchsia-600 to-indigo-900 relative overflow-hidden text-white">
            {/* Background Pattern Overlay */}
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '24px 24px' }}></div>

            {/* Animated Background Elements */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
                opacity: [0.05, 0.1, 0.05]
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -mr-32 -mt-32"
            ></motion.div>

            <motion.div
              animate={{
                scale: [1, 1.5, 1],
                x: [0, 50, 0],
                opacity: [0.1, 0.2, 0.1]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute bottom-0 left-0 w-64 h-64 bg-purple-400 rounded-full blur-3xl -ml-20 -mb-20"
            ></motion.div>

            <div className="relative flex flex-col justify-between h-full p-12 z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
                  <div className="w-2 h-2 bg-purple-300 rounded-full animate-pulse shadow-[0_0_8px_rgba(216,180,254,0.8)]"></div>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-100">Citizens Voice Portal</span>
                </div>

                <div className="w-20 h-20 mb-8 bg-gradient-to-tr from-white/20 to-white/5 backdrop-blur-xl border border-white/30 rounded-[2rem] flex items-center justify-center shadow-2xl relative group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-tr from-purple-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <FontAwesomeIcon icon={faBullhorn} className="text-3xl text-white drop-shadow-md group-hover:scale-110 transition-transform duration-500" />
                </div>

                <h2 className="text-4xl font-[1000] tracking-tight mb-4 leading-[1.1] drop-shadow-sm">
                  Empower your <span className="text-purple-300">Voice</span>
                </h2>
                <p className="text-purple-50/80 font-medium tracking-wide text-base max-w-xs leading-relaxed">
                  A dedicated space for citizens to raise concerns, seek support, and drive meaningful community change.
                </p>
              </motion.div>

              <div className="space-y-6">
                <div className="space-y-4">
                  {[
                    { icon: faUser, title: "Share Concerns", desc: "Report community issues", color: "emerald", delay: 0.1 },
                    { icon: faNewspaper, title: "Get Heard", desc: "Connect with authorities", color: "blue", delay: 0.2 },
                    { icon: faBullhorn, title: "Make Impact", desc: "Drive social progress", color: "purple", delay: 0.3 }
                  ].map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: item.delay, duration: 0.5 }}
                      className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 hover:border-white/20 rounded-2xl p-4 transition-all duration-300 cursor-default"
                    >
                      <div className={`w-12 h-12 bg-${item.color}-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                        <FontAwesomeIcon icon={item.icon} className={`text-${item.color}-300 text-xl`} />
                      </div>
                      <div>
                        <span className="font-black tracking-wide text-sm block group-hover:text-purple-300 transition-colors uppercase">{item.title}</span>
                        <p className="text-[11px] text-purple-100/60 uppercase tracking-widest font-bold font-sans">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="pt-8 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-purple-100/60">
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-xs" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">Privacy Focused</span>
                  </div>
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="w-6 h-6 rounded-full border-2 border-purple-800 bg-purple-600 flex items-center justify-center text-[8px] font-bold">
                        {i}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Login Form */}
          <div className="w-full lg:w-7/12 p-8 sm:p-12 flex flex-col justify-center bg-white relative">
            {/* Mobile Hearing - Panel Name */}
            <div className="lg:hidden text-center mb-6 border-b border-slate-100 pb-4">
              <div className="w-12 h-12 mx-auto mb-3 bg-purple-50 rounded-2xl flex items-center justify-center shadow-inner">
                <FontAwesomeIcon icon={faBullhorn} className="text-xl text-purple-600" />
              </div>
              <h2 className="text-2xl font-[1000] text-purple-900 uppercase tracking-tight">
                Raise Your Voice
              </h2>
            </div>

            <div className="mb-8 group">
              <h1 className="text-3xl sm:text-4xl font-[1000] text-slate-900 tracking-tighter mb-4 uppercase leading-[1.1]">
                Sign In <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-fuchsia-500">Voice Platform</span>
              </h1>
              <div className="w-12 h-1.5 bg-purple-600 rounded-full mb-4 transition-all duration-500 group-hover:w-20"></div>
              <p className="text-sm font-bold text-slate-500 tracking-wide flex items-center gap-2">
                <span className="w-1 h-4 bg-purple-600 rounded-full"></span>
                Secure access to your Raise Your Voice platform
              </p>
            </div>

            {step === "login" && (
              <form className="space-y-4" onSubmit={handleSubmit}>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-xs font-medium"
                  >
                    {error}
                  </motion.div>
                )}

                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                    Email Address
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faEnvelope} className="text-slate-400 group-focus-within:text-purple-500 transition-colors" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-purple-50 focus:border-purple-500 outline-none transition-all text-sm font-bold text-slate-900"
                      placeholder="Enter your registered email address"
                    />
                  </div>
                </div>

                <div className="mt-6 pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-lg shadow-purple-200/50 text-sm font-black text-white bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 active:scale-[0.98] transition-all uppercase tracking-widest ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </button>
                </div>

                <div className="text-center mt-5">
                  <span className="text-xs font-bold text-slate-500">Don't have an account? </span>
                  <Link to="/raise/register" className="text-purple-600 hover:text-purple-700 font-black text-xs uppercase tracking-widest pl-1">
                    Sign up
                  </Link>
                </div>
              </form>
            )}

            {step === "otp" && (
              <form className="space-y-4" onSubmit={handleOtpSubmit}>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-xs font-medium"
                  >
                    {error}
                  </motion.div>
                )}

                <div className="space-y-1.5">
                  <label htmlFor="otp" className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                    Enter OTP
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-slate-400 group-focus-within:text-purple-500 transition-colors" />
                    </div>
                    <input
                      id="otp"
                      name="otp"
                      type="text"
                      required
                      value={otp}
                      onChange={handleOtpChange}
                      className="block w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-purple-50 focus:border-purple-500 outline-none transition-all text-sm font-bold text-slate-900"
                      placeholder="Enter the OTP sent to your email"
                    />
                  </div>
                </div>

                <div className="mt-6 pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-lg shadow-purple-200/50 text-sm font-black text-white bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 active:scale-[0.98] transition-all uppercase tracking-widest ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Verifying...
                      </>
                    ) : (
                      'Verify OTP & Login'
                    )}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-8 pt-6 border-t-2 border-slate-50">
              {/* Quick Login Buttons */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Alternative Access</p>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <Link
                    to="/login"
                    className="group flex flex-col items-center justify-center p-3 bg-slate-50 border-2 border-slate-100 hover:bg-indigo-50 hover:border-indigo-200 rounded-xl transition-all"
                  >
                    <FontAwesomeIcon icon={faUser} className="text-slate-400 group-hover:text-indigo-500 mb-1 transition-colors" />
                    <span className="text-[9px] font-black text-slate-500 group-hover:text-indigo-600 uppercase tracking-widest">General</span>
                  </Link>

                  <Link
                    to="/press/login"
                    className="group flex flex-col items-center justify-center p-3 bg-slate-50 border-2 border-slate-100 hover:bg-teal-50 hover:border-teal-200 rounded-xl transition-all"
                  >
                    <FontAwesomeIcon icon={faCamera} className="text-slate-400 group-hover:text-teal-500 mb-1 transition-colors" />
                    <span className="text-[9px] font-black text-slate-500 group-hover:text-teal-600 uppercase tracking-widest">Press</span>
                  </Link>

                  <Link
                    to="/podcast/login"
                    className="group flex flex-col items-center justify-center p-3 bg-slate-50 border-2 border-slate-100 hover:bg-emerald-50 hover:border-emerald-200 rounded-xl transition-all"
                  >
                    <FontAwesomeIcon icon={faMicrophone} className="text-slate-400 group-hover:text-emerald-500 mb-1 transition-colors" />
                    <span className="text-[9px] font-black text-slate-500 group-hover:text-emerald-600 uppercase tracking-widest">Podcast</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaiseLogin;
